/**
 * Pure helpers used to derive initial state from a promo VO inside
 * the TargetingConditions hook tree. Extracted so they can be unit
 * tested in isolation and reused outside the hook.
 */

// Single shared implementation lives in dateFieldUtils (the promo-expiry
// review modal and split date fields use it too); re-exported here so the
// hook tree keeps one import site and the two copies cannot drift.
import { adjustDateForTimezone } from "./dateFieldUtils";

export { adjustDateForTimezone };

/**
 * Returns the initial [from, to] booking date pair from a promo VO,
 * timezone-adjusted using each date's own timezone offset.
 */
export const getInitialBookingDates = ({ promo }) => {
  const from = promo?.rateRule?.ruleVO?.bookingVO?.bookingStartDate
    ? adjustDateForTimezone(
        new Date(promo.rateRule.ruleVO.bookingVO.bookingStartDate)
      )
    : null;
  const to = promo?.rateRule?.ruleVO?.bookingVO?.bookingEndDate
    ? adjustDateForTimezone(
        new Date(promo.rateRule.ruleVO.bookingVO.bookingEndDate)
      )
    : null;
  return [from, to];
};

/**
 * Returns the initial [start, end] stay date pair from a promo VO,
 * timezone-adjusted using each date's own timezone offset.
 */
export const getInitialStayDates = ({ promo }) => {
  const start = promo?.rateRule?.ruleVO?.stayVO?.stayStartDate
    ? adjustDateForTimezone(
        new Date(promo.rateRule.ruleVO.stayVO.stayStartDate)
      )
    : null;
  const end = promo?.rateRule?.ruleVO?.stayVO?.stayEndDate
    ? adjustDateForTimezone(new Date(promo.rateRule.ruleVO.stayVO.stayEndDate))
    : null;
  return [start, end];
};

/**
 * Returns the initial blackout-date list from a promo VO. When the
 * promo has no blackouts, returns a single empty entry so the UI can
 * render a "blank" row.
 */
export const getInitialBlackoutDates = ({ promo }) => {
  let boDates = promo?.rateRule?.ruleVO?.stayVO?.blackOutDateRanges || [];
  if (boDates.length === 0) {
    return [{ id: Date.now(), dates: null, allowStayThrough: true }];
  }
  return boDates.map((bod, idx) => {
    const startDate = bod?.blackOutStartDate
      ? adjustDateForTimezone(new Date(bod.blackOutStartDate))
      : null;
    const endDate = bod?.blackOutEndDate
      ? adjustDateForTimezone(new Date(bod.blackOutEndDate))
      : null;
    return {
      id: Date.now() + idx,
      dates: [startDate, endDate],
      allowStayThrough: bod?.blackOutStayThruEnabled ?? true,
    };
  });
};

/**
 * Resolves the initial lead-time-type radio value ("1" | "2" | "3")
 * from a promo VO.
 */
export const getInitialLtType = ({ promo }) => {
  if (
    promo?.rateRule?.ruleVO?.bookingVO?.minBookingLeadTime &&
    promo?.rateRule?.ruleVO?.bookingVO?.maxBookingLeadTime
  ) {
    return "3";
  } else if (promo?.rateRule?.ruleVO?.bookingVO?.maxBookingLeadTime) {
    return "2";
  }
  return "1";
};

/**
 * Resolves the initial length-of-stay-type radio value ("1" | "2" | "3")
 * from a promo VO.
 */
export const getInitialLosType = ({ promo }) => {
  if (
    promo?.rateRule?.ruleVO?.stayVO?.minLengthOfStay &&
    promo?.rateRule?.ruleVO?.stayVO?.maxLengthOfStay
  ) {
    return "3";
  } else if (promo?.rateRule?.ruleVO?.stayVO?.maxLengthOfStay) {
    return "2";
  }
  return "1";
};

/**
 * Maps a (min, max) pair to the radio-button range type used by the
 * lead-time and length-of-stay UIs.
 */
export const getRangeType = (min, max) => {
  if (min && max) return "3";
  if (max) return "2";
  return "1";
};
